import React from 'react';
import { CheckCircle2Icon, ClockIcon, Loader2Icon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { useRepeatPrescriptionForm } from '../hooks/useRepeatPrescriptionForm';
import type { PrescriptionValues } from '../hooks/useRepeatPrescriptionForm';
import { clinic } from '../data/clinic';
const fields: {
  key: keyof PrescriptionValues;
  label: string;
  type: string;
  optional?: boolean;
  full?: boolean;
  multiline?: boolean;
}[] = [{
  key: 'name',
  label: 'Naam',
  type: 'text'
}, {
  key: 'birthDate',
  label: 'Geboortedatum',
  type: 'date'
}, {
  key: 'email',
  label: 'E-mailadres',
  type: 'email'
}, {
  key: 'phone',
  label: 'Telefoonnummer',
  type: 'tel',
  optional: true
}, {
  key: 'medication',
  label: 'Medicatie en dosering',
  type: 'text',
  full: true,
  multiline: true
}, {
  key: 'pharmacy',
  label: 'Apotheek (naam en plaats)',
  type: 'text',
  full: true
}];
export function RepeatPrescription() {
  const {
    values,
    errors,
    status,
    update,
    submit,
    reset
  } = useRepeatPrescriptionForm();
  return <>
      <PageHeader title="Herhaalmedicatie" crumbs={[{
      label: 'Herhaalmedicatie'
    }]} intro="Heeft uw pijnspecialist medicatie voorgeschreven en heeft u een herhaalrecept nodig? Vraag het hieronder aan." data-id="element-703" data-mp="331lv9uvvdmy" />

      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-[1fr_1.6fr] lg:gap-16 lg:px-8 lg:py-20" data-id="element-704" data-mp="3kkm2me0zk3qh">
        <div data-id="element-705" data-mp="3flhu7plvya17">
          <div className="flex gap-4 rounded-2xl bg-pain/5 p-6 ring-1 ring-pain/20" data-id="element-706" data-mp="3fm13yj1dts45">
            <ClockIcon className="mt-0.5 h-5 w-5 shrink-0 text-pain" aria-hidden="true" data-id="element-707" data-mp="3bil83ox4zvsi" />
            <div data-id="element-708" data-mp="36jrtft645yqr">
              <p className="font-display font-semibold text-ink" data-id="element-709" data-mp="36lcodjwcbjp5">Bestel tijdig</p>
              <p className="mt-1 text-[15px] leading-relaxed text-muted" data-id="element-710" data-mp="31m9vextmx3xj">Reken op 5 werkdagen verwerkingstijd.</p>
            </div>
          </div>
          <p className="mt-8 text-[15px] leading-relaxed text-muted" data-id="element-711" data-mp="14r3ye73mihm2">
            Vragen over uw medicatie? Bel ons op{' '}
            <a href={clinic.phoneHref} className="font-medium text-primary hover:underline" data-id="element-712" data-mp="2uabyya2b0xo7">
              {clinic.phone}
            </a>{' '}
            ({clinic.phoneHours.toLowerCase()}) of mail naar{' '}
            <a href={`mailto:${clinic.email}`} className="font-medium text-primary hover:underline" data-id="element-713" data-mp="2kceb3nocx2w4">
              {clinic.email}
            </a>
            .
          </p>
        </div>

        <div className="rounded-3xl border border-line p-6 sm:p-8" data-id="element-714" data-mp="3d0tmm08a532p">
          {status === 'success' ? <div className="py-10 text-center" data-id="element-715" data-mp="3hzmrj49i3lr9">
              <CheckCircle2Icon className="mx-auto h-12 w-12 text-primary" aria-hidden="true" data-id="element-716" data-mp="3hzed37xqc8dy" />
              <h2 className="mt-5 font-display text-2xl font-semibold text-ink" data-id="element-717" data-mp="33243y58k7ele">Aanvraag ontvangen</h2>
              <p className="mx-auto mt-2 max-w-md text-[15px] text-muted" data-id="element-718" data-mp="3805nssdt7hxx">
                Wij verwerken uw aanvraag binnen 5 werkdagen en sturen het recept naar uw apotheek.
              </p>
              <button type="button" onClick={reset} className="mt-8 rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-ink hover:border-primary hover:text-primary" data-id="element-719" data-mp="1wnd0bts1xfno">
                Nieuwe aanvraag
              </button>
            </div> : <form onSubmit={submit} noValidate className="grid gap-5 sm:grid-cols-2" data-id="element-720" data-mp="nnaerfpzhk7v">
              {fields.map(f => {
            const id = `rx-${f.key}`;
            const error = errors[f.key];
            const cls = `w-full rounded-xl border bg-white px-4 py-3 text-[15px] text-ink focus:outline-none focus:ring-2 focus:ring-primary/20 ${error ? 'border-pain' : 'border-line focus:border-primary'}`;
            return <div key={f.key} className={f.full ? 'sm:col-span-2' : ''} data-id="element-721" data-mp="3w3bx8rewgglc">
                    <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink" data-id="element-722" data-mp="38eaw7p8ec7q3">
                      {f.label} {f.optional && <span className="font-normal text-muted" data-id="element-723" data-mp="34c644uxljj5k">(optioneel)</span>}
                    </label>
                    {f.multiline ? <textarea id={id} rows={3} value={values[f.key]} onChange={e => update(f.key, e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} className={cls} data-id="element-724" data-mp="3m1t6dtvqo6pt" /> : <input id={id} type={f.type} value={values[f.key]} onChange={e => update(f.key, e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} className={cls} data-id="element-725" data-mp="8lv5vf1ajids" />}
                    {error && <p id={`${id}-error`} className="mt-1.5 text-sm text-pain" data-id="element-726" data-mp="2ctybwdaqfs0a">
                        {error}
                      </p>}
                  </div>;
          })}
              <div className="sm:col-span-2" data-id="element-727" data-mp="234ohgqsf1x90">
                <button type="submit" disabled={status === 'submitting'} className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-[15px] font-semibold text-white transition-colors duration-150 hover:bg-primary-dark disabled:opacity-70 sm:w-auto" data-id="element-728" data-mp="1y4trcpggtbp0">
                  {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-729" data-mp="2lw7i4qyywz7v" />}
                  Herhaalrecept aanvragen
                </button>
              </div>
            </form>}
        </div>
      </section>
    </>;
}